import { AdditionalChargeType } from './AdditionalCharges.types';

/**
 * Additional Vendor Charges — Part 1 declaration copy and labels (ISS-291748 / C1).
 *
 * "Incidental" is one of the three charge *types*, not the feature name — every user-facing string
 * here says "additional charge".
 */

/** Display labels for the read-only "Type of charge" beside each selection. */
export const CHARGE_TYPE_LABELS: Record<AdditionalChargeType, string> = {
  INCIDENTAL: 'Incidental',
  SELF_REIMBURSEMENT: 'Self-Reimbursement',
  THIRD_PARTY_REIMBURSEMENT: 'Third-Party Reimbursement',
};

export const ADDITIONAL_CHARGES_LABELS = {
  /**
   * Per-row label. `Additional charge`, never `Incidental charge`: a row can resolve to any of the
   * three types once a charge is picked (Incidental is only one of them), and the spec's naming rule
   * forbids using a type name as the feature label.
   */
  chargeRow: 'Additional charge',
  chargeType: 'Type of charge',
  chargePlaceholder: 'Select charge...',
  addRow: 'Add More',
  removeRow: 'Remove this charge',
  /** Declares the selected charges and advances from Part 1 to the Part 2 grids. */
  next: 'Next',
};

export const ADDITIONAL_CHARGES_MESSAGES = {
  /** Blocks the task's Submit until at least one charge is declared. */
  noChargeSelected: 'Please select at least one additional charge.',
  fetchFailed: 'Unable to load the additional charge catalog.',
  /**
   * A seed-data gap (D1) — the org has no active charges for this vendor type. Deliberately worded
   * differently from `missingVendorType` below: one is missing data, the other missing wiring, and a
   * shared message would make them indistinguishable from a screenshot.
   */
  emptyMaster: (vendorType: string) =>
    `No additional charges are configured for ${vendorType}.`,
  /** A task-wiring gap (A2) — the field arrived without a vendor type, so no catalog can be read. */
  missingVendorType:
    'This task is missing its vendor type, so additional charges cannot be loaded. Please contact support.',
  /** A task-wiring gap in the FE plumbing (see `AdditionalChargesExtraData.taskId`), not a backend gap. */
  missingTaskId:
    'This task is missing its task reference, so additional charges cannot be progressed. Please contact support.',
  noChargesDeclared: 'No additional charges were declared.',
  /** Surfaced when `POST /declare` fails — the vendor stays on Part 1 rather than a blank Part 2. */
  declareFailed: 'Unable to save the declared additional charges.',
  /** Surfaced when the Part 1<->Part 2 re-entry check (`GET /lines`) fails. Fails open to Part 1. */
  reentryCheckFailed: 'Unable to check for previously declared additional charges.',
};
